/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnChanges, type OnInit, type SimpleChanges, ChangeDetectorRef, Component, Injector, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { type ColumnDef, type TableConfig, DataTableComponent, I18nService, TableAbstract, UNPLACEHOLDER } from '@dm/core';
import { eventType, infigo } from '@dm/live-material-infigo';
import { type MappingView } from 'app/common/types/mo';
import { FeatureConfig } from 'app/config/feature';
import { MappingStrService } from 'app/feature/service/block-service/hosts/mapping.service';
import { type SearchObj } from 'app/feature/share/global-search/global-search-str.service';
import { EventEmitter } from 'events';

import { InfigoService } from '../../../infigo.service';

@Component({
    selector: 'app-mapping-table',
    templateUrl: './mapping-table.component.html',
    styleUrls: ['./mapping-table.component.scss'],
})
export class MappingTableComponent extends TableAbstract<any> implements OnInit, OnChanges, AfterViewInit {
    @Input() data: MappingView[];
    @Input() type;
    @Input() loading = false;
    @ViewChild('dt') tableView: DataTableComponent;
    @ViewChild('lunLinkTemp') lunLinkTemp: TemplateRef<any>;
    @Output() exportSearchItem = new EventEmitter();
    useData: MappingView[];
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    // 展开表格过滤参数
    filterParamsMap = {};
    expandMap = {};
    searchItem;
    jumpPath: SearchObj;
    associateTypeMap = {};
    useDataMap: Array<{ match: any; type: string[] | string }>;

    constructor(private i18n: I18nService,
        public infigoService: InfigoService,
        private mappingStr: MappingStrService,
        public changeDetectorRef: ChangeDetectorRef,
        public injector: Injector) {
        super(injector);
        this.associateTypeMap = {
            lun: i18n.get('common.host_title'),
            lunGroup: `${i18n.get('common.hostGroup_list_title')}/${i18n.get('common.host_title')}`,
            host: `${i18n.get('common.objectType_LUN')}/${i18n.get('common.lungroup_checkbox')}`,
            hostGroup: i18n.get('common.lungroup_checkbox'),
        };
        this.useDataMap = [
            {
                type: ['lun', 'lunGroup'],
                match: [
                    {
                        use: (item): boolean => item.hostName && UNPLACEHOLDER !== item.hostName,
                        transform: (item): { [key: string]: string } => ({
                            name: item.hostName,
                            actualId: item.hostId,
                            actualIdType: 'hostId',
                            type: 'host',
                        }),
                    }, {
                        use: (item): boolean => item.hostGroupName && UNPLACEHOLDER !== item.hostGroupName,
                        transform: (item): { [key: string]: string } => ({
                            name: item.hostGroupName,
                            actualId: item.hostGroupId,
                            actualIdType: 'hostGroupId',
                            type: 'hostGroup',
                        }),
                    },
                ],
            },
            {
                type: ['host', 'hostGroup'],
                match: [
                    {
                        use: (item): boolean => item.lunName && UNPLACEHOLDER !== item.lunName,
                        transform: (item): { [key: string]: string } => ({
                            name: item.lunName,
                            actualId: item.lunId,
                            actualIdType: 'lunId',
                            type: FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespace' : 'lun',
                        }),
                    }, {
                        use: (item): boolean => item.lunGroupName && UNPLACEHOLDER !== item.lunGroupName,
                        transform: (item): { [key: string]: string } => ({
                            name: item.lunGroupName,
                            actualId: item.lunGroupId,
                            actualIdType: 'lunGroupId',
                            type: FeatureConfig.IsReplaceStrLunWithNamespace ? 'NamespaceGroup' : 'lunGroup',
                        }),
                    },
                ],
            },
        ];
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    ngAfterViewInit(): void {
        this.initTable();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.data) {
            this.useData = this.handleMappingData(changes.data.currentValue);
        }
    }

    initBtns(): void {
    }

    initTable(): void {
        this.tableColumnDefs = [
            {
                field: 'associateType',
                header: this.i18n.get('common.type_column'),
            },
            {
                field: 'portGroupName',
                header: this.i18n.get('common.portGroup_title'),
                render: (value): string => value || UNPLACEHOLDER,
            },
            {
                field: 'displayedMappingType',
                header: this.i18n.get('host.host_view_toplogy_mappingType_lab'),
            },
        ];
        this.tableColumnDefs.unshift(/host/i.test(this.type) ? {
            field: 'displayedName',
            header: this.i18n.get('common.name_column'),
            filterType: 'search',
            customTemplate: this.lunLinkTemp,
        } : {
            field: 'displayedName',
            header: this.i18n.get('common.name_column'),
            filterType: 'search',
            render: (value): string => this.searchItem = value,
            onClick: (data: any): void => this.setRouterPath(data),
        });
        this.tableConfig = {
            dataKey: 'ID',
            motion: false,
            rows: 5,
            beforeShow: (data) => this.handleMappingData(data),
        };
    }

    getMatchData(item): any {
        const matchData = this.useDataMap.find((i) => i.type.includes(this.type));

        if (!matchData) {
            return {};
        }

        const { match } = matchData;
        const matchItem = match.find((i) => i.use(item));

        if (!matchItem) {
            return {};
        }

        return matchItem.transform(item);
    }

    handleMappingData(data): any {
        return data.map((item) => {
            const { name, actualId, actualIdType, type } = this.getMatchData(item);
            if (this.associateTypeMap[this.type] && this.type !== 'host') {
                item.associateType = this.associateTypeMap[this.type];
            } else if (this.associateTypeMap[this.type] && this.type === 'host') {
                if (item.lunGroupId !== '') {
                    item.associateType = this.i18n.get('common.lungroup_checkbox');
                } else if (item.lunId !== '') {
                    item.associateType = this.i18n.get('common.objectType_LUN');
                }
            }
            return {
                ...item,
                displayedName: name,
                displayedMappingType: this.mappingStr.mappingTypeStr[item.mappingType],
                __actualId: actualId,
                __actualIdType: actualIdType,
                type,
            };
        });
    }

    // 保存展开表格的过滤参数
    saveFilterParams(event: { lunGroupId: string | number }): void {
        this.filterParamsMap[event.lunGroupId] = event;
    }

    onRowExpand(rowdata: { expand: any; data: any }): void {
        const { expand, data } = rowdata;
        this.expandMap[data.lunGroupId] = expand;
        // 展开项
        const expandItem = 'lunGroup_' + data.lunGroupId;
        this.tableView.table.expandLoadMap[expandItem] = expand ? true : undefined;
    }

    onClickExpand(data: any, index: number): void {
        this.tableView.table.clickExpand(data, index);
    }

    setRouterPath(value: { __actualId: any; displayedName: any; type: any }): void {
        this.jumpPath = {
            id: value.__actualId,
            name: value.displayedName,
            precisionType: value.type,
            COMBINATION: '',
        };
        this.jumpToPath();
    }
    jumpToPath(data = null): void {
        const routerEvent = this.infigoService.transformSearchParams(data || this.jumpPath);
        infigo.dispatch(eventType.CLICK_LIST_ITEM, routerEvent);
    }
}
